/**
 * BYOK key grants.
 *
 * The `Rust SecretVault` is zero-knowledge: secret envelopes are sealed to the tenant's
 * app-scoped key, which never leaves the client. So a BYOK credential reaches PVR3 in two
 * steps: ask for a short-lived grant, then seal **only that credential** to it.
 *
 * Construction must match `crates/pvr3-orchestrator/src/keygrant.rs`:
 * X25519 ECDH → HKDF-SHA256 (salt = client_eph_pub || server_pub, info = AAD) → AES-256-GCM.
 * AES-GCM (not the vault's ChaCha20-Poly1305) because WebCrypto has it everywhere.
 */

export interface KeyGrantOffer {
  key_id: string;
  public_key_b64: string;
  expires_at: string;
  aad: string;
}

export interface KeyGrantEnvelope {
  key_id: string;
  client_ephemeral_public_key_b64: string;
  nonce_b64: string;
  ciphertext_b64: string;
}

const encoder = new TextEncoder();

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

async function importRawX25519Public(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw as BufferSource, { name: "X25519" }, true, []);
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/** HKDF-SHA256(shared, salt = client_pub || server_pub, info = aad) → AES-256-GCM key. */
async function deriveGrantKey(
  sharedBits: ArrayBuffer,
  clientPub: Uint8Array,
  serverPub: Uint8Array,
  aad: string
): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", sharedBits, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: concat(clientPub, serverPub) as BufferSource,
      info: encoder.encode(aad) as BufferSource,
    },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );
}

/** Seals a credential to a grant (one-off ephemeral key, single use). */
export async function sealToKeyGrant(
  offer: KeyGrantOffer,
  credential: string
): Promise<KeyGrantEnvelope> {
  const serverPubRaw = base64ToBytes(offer.public_key_b64);
  const serverPub = await importRawX25519Public(serverPubRaw);

  const clientKeyPair = (await crypto.subtle.generateKey({ name: "X25519" }, true, [
    "deriveBits",
  ])) as CryptoKeyPair;

  const clientPubRaw = new Uint8Array(
    await crypto.subtle.exportKey("raw", clientKeyPair.publicKey)
  );

  const sharedBits = await crypto.subtle.deriveBits(
    { name: "X25519", public: serverPub },
    clientKeyPair.privateKey,
    256
  );

  const key = await deriveGrantKey(sharedBits, clientPubRaw, serverPubRaw, offer.aad);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource, additionalData: encoder.encode(offer.aad) as BufferSource },
    key,
    encoder.encode(credential)
  );

  return {
    key_id: offer.key_id,
    client_ephemeral_public_key_b64: bytesToBase64(clientPubRaw),
    nonce_b64: bytesToBase64(nonce),
    ciphertext_b64: bytesToBase64(new Uint8Array(ciphertext)),
  };
}
